/*
  Quantity stepper — document-delegated event handlers.

  Why delegation (not per-element listeners): the cart drawer is re-rendered
  via the Section Rendering API after every mutation (assets/mug-cart.js
  swapDrawerHTML). Per-element listeners attached during a one-time init
  pass die with the old DOM, leaving the swapped-in stepper inert. With
  delegation, a single document-level listener works on any current OR
  future stepper markup without needing re-init hooks.

  Markup contract (snippets/quantity-stepper.liquid):
    [data-quantity-stepper] container with optional data-min / data-max
      ├── button [data-quantity-action="decrement"]
      ├── input  [data-quantity-input]
      └── button [data-quantity-action="increment"]
*/

(() => {
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  const readBounds = (stepper, input) => ({
    min: Number(stepper.dataset.min ?? input.min ?? 1),
    max: Number(stepper.dataset.max ?? input.max ?? 99),
  });

  // − / + clicks: mutate input.value and fire a synthetic change event.
  // bubbles:true is critical — it lets the document-delegated change
  // listener in mug-cart.js see the event and POST /cart/change.js.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quantity-action]');
    if (!btn) return;
    const stepper = btn.closest('[data-quantity-stepper]');
    const input = stepper?.querySelector('[data-quantity-input]');
    if (!input) return;

    const { min, max } = readBounds(stepper, input);
    const current = Number(input.value) || min;
    const delta = btn.dataset.quantityAction === 'increment' ? 1 : -1;
    const next = clamp(current + delta, min, max);
    if (next === Number(input.value)) return;

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Clamp validator on direct typing (rare, but catches paste / arrow keys).
  // Doesn't re-dispatch — the original change event already bubbled.
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-quantity-input]');
    if (!input) return;
    const stepper = input.closest('[data-quantity-stepper]');
    if (!stepper) return;

    const { min, max } = readBounds(stepper, input);
    const n = Number(input.value);
    if (!Number.isFinite(n)) {
      input.value = String(min);
      return;
    }
    const clamped = clamp(n, min, max);
    if (clamped !== n) input.value = String(clamped);
  });
})();
